/**
 * User Settings Page
 * 
 * Manage user account and preferences with tabbed interface:
 * - Account tab: Email, password management
 * - Preferences tab: Default account, pagination settings
 * - Shared Access tab: Manage secondary users with granular permissions
 * 
 * @validates Requirements - User settings management
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useUserSettingsStore } from '../store/userSettingsStore';
import { useAccountStore } from '../store/accountStore';
import { Card, Button, Input } from '../components/ui';
import { SharedAccessTab, SubscriptionTab, AlertsTab } from '../components/settings';
import { PAGE_SIZE_MIN, PAGE_SIZE_MAX, PAGE_SIZE_DEFAULT, STATISTICS_CATEGORIES_MIN, STATISTICS_CATEGORIES_MAX, STATISTICS_CATEGORIES_DEFAULT } from '@budgira/shared';
import { useAuthStore } from '../store/authStore';
import CategoriesPage from './CategoriesPage';
import PaymentMethodsPage from './PaymentMethodsPage';
import CounterpartiesPage from './CounterpartiesPage';
import ApiKeysPage from './ApiKeysPage';
import { validatePassword } from '../utils/passwordValidator';
import { authService } from '../services/auth.service';
import { userSettingsService } from '../services/user-settings.service';

type TabType = 'account' | 'preferences' | 'subscription' | 'alerts' | 'categories' | 'paymentMethods' | 'counterparties' | 'sharedAccess' | 'apiKeys';

const VALID_TABS: TabType[] = ['account', 'preferences', 'subscription', 'alerts', 'categories', 'paymentMethods', 'counterparties', 'sharedAccess', 'apiKeys'];

const UserSettingsPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const initialTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'account';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const { canManageSharedAccess, canManageCategories, canManagePaymentMethods, logout } = useAuthStore();
  
  // Sync activeTab with URL parameter when it changes
  useEffect(() => {
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, activeTab]);
  // User settings store
  const {
    profile,
    preferences,
    isLoadingProfile,
    isLoadingPreferences,
    isSaving,
    error,
    fetchProfile,
    fetchPreferences,
    updatePassword,
    updatePreferences,
    clearError,
  } = useUserSettingsStore();
  
  // Account store for default account selection
  const { accounts, fetchAccounts, selectAccount } = useAccountStore();
  
  // Form states for Account tab
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    currentPassword: '',
  });
  const [pendingEmailChange, setPendingEmailChange] = useState<{ newEmail: string; expiresAt: string } | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  
  // Local state for Preferences tab (before saving)
  const [localPreferences, setLocalPreferences] = useState({
    defaultAccountId: '' as string | undefined,
    operationsPageSize: PAGE_SIZE_DEFAULT,
    schedulersPageSize: PAGE_SIZE_DEFAULT,
    statisticsCategoriesCount: STATISTICS_CATEGORIES_DEFAULT,
    darkMode: false,
  });
  const [preferencesModified, setPreferencesModified] = useState(false);
  const [preferencesSuccess, setPreferencesSuccess] = useState(false);
  
  // Account deletion state
  const [isLoadingDeletion, setIsLoadingDeletion] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletionError, setDeletionError] = useState<string | null>(null);
  const [deletionDelayDays, setDeletionDelayDays] = useState<number>(30);
  
  // Load data on mount
  useEffect(() => {
    fetchProfile();
    fetchPreferences();
    fetchAccounts();
    loadPendingEmailChange();
    loadDeletionDelay();
  }, [fetchProfile, fetchPreferences, fetchAccounts]);

  // Load pending email change
  const loadPendingEmailChange = async () => {
    try {
      const response = await authService.getPendingEmailChange();
      if (response.success && response.data) {
        setPendingEmailChange(response.data.pending);
      }
    } catch (err) {
      console.error('Failed to load pending email change:', err);
    }
  };

  // Load deletion delay setting
  const loadDeletionDelay = async () => {
    try {
      const response = await userSettingsService.getDeletionDelay();
      if (response.success && response.data) {
        setDeletionDelayDays(response.data.delayDays);
      }
    } catch (err) {
      console.error('Failed to load deletion delay:', err);
    }
  };

  // Request account deletion
  const handleRequestDeletion = async () => {
    setIsLoadingDeletion(true);
    setDeletionError(null);
    
    try {
      const response = await userSettingsService.requestDeletion();
      if (response.success && response.data) {
        // Account deletion requested - log the user out immediately
        // The account is now blocked and the user cannot access it anymore
        logout();
        // Note: The user will be redirected to the login page by the logout function
      } else {
        setDeletionError(response.error?.message || t('errors.generic'));
      }
    } catch (err) {
      console.error('Failed to request deletion:', err);
      setDeletionError(t('errors.generic'));
    } finally {
      setIsLoadingDeletion(false);
    }
  };

  // Initialize local preferences when preferences are loaded
  useEffect(() => {
    if (preferences) {
      setLocalPreferences({
        defaultAccountId: preferences.defaultAccountId,
        operationsPageSize: preferences.operationsPageSize,
        schedulersPageSize: preferences.schedulersPageSize,
        statisticsCategoriesCount: preferences.statisticsCategoriesCount ?? STATISTICS_CATEGORIES_DEFAULT,
        darkMode: preferences.darkMode ?? false,
      });
      setPreferencesModified(false);
      
      // Sync theme with saved preference
      if (preferences.darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, [preferences]);

  // Revert dark mode to saved state when leaving preferences tab without saving
  useEffect(() => {
    if (activeTab !== 'preferences' && preferencesModified && preferences) {
      // Revert theme to saved state
      if (preferences.darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      // Reset local state
      setLocalPreferences(prev => ({
        ...prev,
        darkMode: preferences.darkMode ?? false,
      }));
      setPreferencesModified(false);
    }
  }, [activeTab, preferencesModified, preferences]);

  // ============================================
  // HANDLERS - ACCOUNT TAB
  // ============================================

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setEmailSuccess(false);
    
    if (!emailForm.newEmail.trim()) {
      setEmailError(t('settings.account.email.required'));
      return;
    }
    
    // Check if email is same as current
    if (profile && emailForm.newEmail.trim().toLowerCase() === profile.email.toLowerCase()) {
      setEmailError(t('settings.account.email.sameAsCurrent'));
      return;
    }
    
    if (!emailForm.currentPassword) {
      setEmailError(t('settings.account.email.passwordRequired'));
      return;
    }
    
    setIsSubmittingEmail(true);
    
    try {
      const response = await authService.requestEmailChange(
        emailForm.newEmail.trim(),
        emailForm.currentPassword
      );
      
      if (response.success) {
        setEmailSuccess(true);
        setEmailForm({ newEmail: '', currentPassword: '' });
        // Reload pending change
        await loadPendingEmailChange();
      } else {
        const errorCode = response.error?.code;
        switch (errorCode) {
          case 'INVALID_CREDENTIALS':
            setEmailError(t('settings.account.email.invalidPassword'));
            break;
          case 'EMAIL_ALREADY_EXISTS':
            setEmailError(t('settings.account.email.alreadyExists'));
            break;
          default:
            setEmailError(response.error?.message || t('errors.generic'));
        }
      }
    } catch (err: unknown) {
      // Handle axios errors (HTTP error responses)
      console.error('Full error object:', err);
      const axiosError = err as { response?: { data?: { success?: boolean; error?: { code?: string; message?: string } }; status?: number } };
      console.error('Error response data:', axiosError.response?.data);
      console.error('Error response status:', axiosError.response?.status);
      const errorCode = axiosError.response?.data?.error?.code;
      console.error('Error code extracted:', errorCode);
      
      switch (errorCode) {
        case 'INVALID_CREDENTIALS':
          setEmailError(t('settings.account.email.invalidPassword'));
          break;
        case 'EMAIL_ALREADY_EXISTS':
          setEmailError(t('settings.account.email.alreadyExists'));
          break;
        case 'VALIDATION_ERROR':
          // Check if it's the pending email change error
          const errorMessage = axiosError.response?.data?.error?.message || '';
          if (errorMessage.toLowerCase().includes('pending')) {
            setEmailError(t('settings.account.email.alreadyPending'));
          } else {
            setEmailError(errorMessage || t('errors.generic'));
          }
          break;
        default:
          console.error('Falling through to default, errorCode was:', errorCode);
          setEmailError(t('errors.generic'));
      }
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  const handleCancelEmailChange = async () => {
    try {
      const response = await authService.cancelEmailChange();
      if (response.success) {
        setPendingEmailChange(null);
      }
    } catch (err) {
      console.error('Failed to cancel email change:', err);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);
    
    if (!passwordForm.currentPassword) {
      setPasswordError(t('settings.account.password.currentRequired'));
      return;
    }
    
    if (!passwordForm.newPassword) {
      setPasswordError(t('settings.account.password.newRequired'));
      return;
    }
    
    // Full password validation (same rules as registration)
    const passwordValidation = validatePassword(passwordForm.newPassword);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.error);
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('settings.account.password.mismatch'));
      return;
    }
    
    const success = await updatePassword({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
    
    if (success) {
      setPasswordSuccess(true);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } else {
      setPasswordError(error || t('errors.generic'));
    }
  };

  // ============================================
  // HANDLERS - PREFERENCES TAB
  // ============================================

  const handleDefaultAccountChange = (accountId: string) => {
    setLocalPreferences(prev => ({
      ...prev,
      defaultAccountId: accountId || undefined,
    }));
    setPreferencesModified(true);
    setPreferencesSuccess(false);
  };

  const handlePageSizeChange = (pageSize: number) => {
    // Clamp value within valid range
    const clampedSize = Math.min(PAGE_SIZE_MAX, Math.max(PAGE_SIZE_MIN, pageSize));
    setLocalPreferences(prev => ({
      ...prev,
      operationsPageSize: clampedSize,
    }));
    setPreferencesModified(true);
    setPreferencesSuccess(false);
  };

  const handleSchedulersPageSizeChange = (pageSize: number) => {
    // Clamp value within valid range
    const clampedSize = Math.min(PAGE_SIZE_MAX, Math.max(PAGE_SIZE_MIN, pageSize));
    setLocalPreferences(prev => ({
      ...prev,
      schedulersPageSize: clampedSize,
    }));
    setPreferencesModified(true);
    setPreferencesSuccess(false);
  };

  const handleStatisticsCategoriesCountChange = (count: number) => {
    // Clamp value within valid range
    const clampedCount = Math.min(STATISTICS_CATEGORIES_MAX, Math.max(STATISTICS_CATEGORIES_MIN, count));
    setLocalPreferences(prev => ({
      ...prev,
      statisticsCategoriesCount: clampedCount,
    }));
    setPreferencesModified(true);
    setPreferencesSuccess(false);
  };

  const handleDarkModeChange = (enabled: boolean) => {
    setLocalPreferences(prev => ({
      ...prev,
      darkMode: enabled,
    }));
    setPreferencesModified(true);
    setPreferencesSuccess(false);
    
    // Apply theme immediately for instant feedback
    if (enabled) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handlePreferencesSave = async () => {
    setPreferencesSuccess(false);
    
    const success = await updatePreferences({
      defaultAccountId: localPreferences.defaultAccountId || null,
      operationsPageSize: localPreferences.operationsPageSize,
      schedulersPageSize: localPreferences.schedulersPageSize,
      statisticsCategoriesCount: localPreferences.statisticsCategoriesCount,
      darkMode: localPreferences.darkMode,
    });
    
    if (success) {
      setPreferencesModified(false);
      setPreferencesSuccess(true);
      
      // Update selected account immediately if default account changed
      if (localPreferences.defaultAccountId) {
        selectAccount(localPreferences.defaultAccountId);
      }
    }
  };

  const handlePreferencesCancel = () => {
    if (preferences) {
      setLocalPreferences({
        defaultAccountId: preferences.defaultAccountId,
        operationsPageSize: preferences.operationsPageSize,
        schedulersPageSize: preferences.schedulersPageSize,
        statisticsCategoriesCount: preferences.statisticsCategoriesCount ?? STATISTICS_CATEGORIES_DEFAULT,
        darkMode: preferences.darkMode ?? false,
      });
      
      // Revert theme to saved state
      if (preferences.darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
    setPreferencesModified(false);
    setPreferencesSuccess(false);
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderTabs = () => (
    <div className="flex flex-wrap border-b border-slate-200 dark:border-slate-700 mb-6">
      <button
        onClick={() => { setActiveTab('account'); clearError(); setEmailError(null); setPasswordError(null); }}
        className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
          activeTab === 'account'
            ? 'border-violet-500 text-violet-600 dark:text-violet-400'
            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
        }`}
      >
        {t('settings.tabs.account')}
      </button>
      <button
        onClick={() => { setActiveTab('preferences'); clearError(); setEmailError(null); setPasswordError(null); }}
        className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
          activeTab === 'preferences'
            ? 'border-violet-500 text-violet-600 dark:text-violet-400'
            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
        }`}
      >
        {t('settings.tabs.preferences')}
      </button>
      {/* Subscription tab */}
      <button
        onClick={() => { setActiveTab('subscription'); clearError(); setEmailError(null); setPasswordError(null); }}
        className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
          activeTab === 'subscription'
            ? 'border-violet-500 text-violet-600 dark:text-violet-400'
            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
        }`}
      >
        {t('settings.tabs.subscription')}
      </button>
      {/* Alerts tab */}
      <button
        onClick={() => { setActiveTab('alerts'); clearError(); setEmailError(null); setPasswordError(null); }}
        className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
          activeTab === 'alerts'
            ? 'border-violet-500 text-violet-600 dark:text-violet-400'
            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
        }`}
      >
        {t('settings.tabs.alerts', 'Alertes')}
      </button>
      {/* Categories tab - only for users with permission */}
      {canManageCategories() && (
        <button
          onClick={() => { setActiveTab('categories'); clearError(); setEmailError(null); setPasswordError(null); }}
          className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'categories'
              ? 'border-violet-500 text-violet-600 dark:text-violet-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
          }`}
        >
          {t('settings.tabs.categories')}
        </button>
      )}
      {/* Payment methods tab - only for users with permission */}
      {canManagePaymentMethods() && (
        <button
          onClick={() => { setActiveTab('paymentMethods'); clearError(); setEmailError(null); setPasswordError(null); }}
          className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'paymentMethods'
              ? 'border-violet-500 text-violet-600 dark:text-violet-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
          }`}
        >
          {t('settings.tabs.paymentMethods')}
        </button>
      )}
      {/* Counterparties tab - only for users with permission */}
      {canManageCategories() && (
        <button
          onClick={() => { setActiveTab('counterparties'); clearError(); setEmailError(null); setPasswordError(null); }}
          className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'counterparties'
              ? 'border-violet-500 text-violet-600 dark:text-violet-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
          }`}
        >
          {t('settings.tabs.counterparties')}
        </button>
      )}
      {/* Hide shared access tab for secondary users and free users */}
      {canManageSharedAccess() && (
        <button
          onClick={() => { setActiveTab('sharedAccess'); clearError(); setEmailError(null); setPasswordError(null); }}
          className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'sharedAccess'
              ? 'border-violet-500 text-violet-600 dark:text-violet-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
          }`}
        >
          {t('settings.tabs.sharedAccess')}
        </button>
      )}
      {/* API Keys tab */}
      <button
        onClick={() => { setActiveTab('apiKeys'); clearError(); setEmailError(null); setPasswordError(null); }}
        className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
          activeTab === 'apiKeys'
            ? 'border-violet-500 text-violet-600 dark:text-violet-400'
            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
        }`}
      >
        {t('settings.tabs.apiKeys')}
      </button>
    </div>
  );

  const renderAccountTab = () => (
    <div className="space-y-8">
      {/* Email Section */}
      <Card>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('settings.account.email.title')}
        </h3>
        
        {/* Current email display */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t('settings.account.email.currentLabel')}
          </label>
          <div className="px-4 py-3 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-900 dark:text-slate-100">
            {profile?.email || '-'}
          </div>
        </div>

        {/* Pending email change notice */}
        {pendingEmailChange && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-amber-800 dark:text-amber-300 font-medium">
                  {t('settings.account.email.pendingChange')}
                </p>
                <p className="text-amber-700 dark:text-amber-400 text-sm mt-1">
                  {t('settings.account.email.pendingEmail')}: <strong>{pendingEmailChange.newEmail}</strong>
                </p>
                <p className="text-amber-600 dark:text-amber-500 text-xs mt-1">
                  {t('settings.account.email.expiresAt')}: {new Date(pendingEmailChange.expiresAt).toLocaleString()}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCancelEmailChange}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}

        {/* Change email form */}
        {!pendingEmailChange && (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <Input
              label={t('settings.account.email.newLabel')}
              type="email"
              value={emailForm.newEmail}
              onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
              placeholder={t('settings.account.email.newPlaceholder')}
              disabled={isSubmittingEmail}
            />
            <Input
              label={t('settings.account.email.currentPassword')}
              type="password"
              value={emailForm.currentPassword}
              onChange={(e) => setEmailForm(prev => ({ ...prev, currentPassword: e.target.value }))}
              placeholder={t('settings.account.email.currentPasswordPlaceholder')}
              disabled={isSubmittingEmail}
            />
            {emailError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                {emailError}
              </div>
            )}
            {emailSuccess && (
              <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm">
                {t('settings.account.email.confirmationSent')}
              </div>
            )}
            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmittingEmail}>
                {isSubmittingEmail ? t('common.saving') : t('settings.account.email.save')}
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Password Section */}
      <Card>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('settings.account.password.title')}
        </h3>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <Input
            label={t('settings.account.password.current')}
            type="password"
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
            disabled={isSaving}
          />
          <Input
            label={t('settings.account.password.new')}
            type="password"
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
            disabled={isSaving}
            helperText={t('settings.account.password.minLength')}
          />
          <Input
            label={t('settings.account.password.confirm')}
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
            disabled={isSaving}
          />
          {passwordError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm">
              {t('settings.account.password.success')}
            </div>
          )}
          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? t('common.saving') : t('settings.account.password.save')}
            </Button>
          </div>
        </form>
      </Card>

      {/* Account Deletion Section - Hide for admin users */}
      {profile?.role !== 'admin' && (
        <Card>
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
            {t('settings.account.deletion.title')}
          </h3>
          
          {showDeleteConfirm ? (
            // Confirmation step
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-amber-800 dark:text-amber-300 font-medium mb-2">
                  {t('settings.account.deletion.warningTitle')}
                </p>
                <ul className="list-disc list-inside text-amber-700 dark:text-amber-400 text-sm space-y-1">
                  <li>{t('settings.account.deletion.warningImmediate')}</li>
                  <li>{t('settings.account.deletion.warningExport')}</li>
                  <li>{t('settings.account.deletion.warningData', { days: deletionDelayDays })}</li>
                  <li>{t('settings.account.deletion.warningContact', { days: deletionDelayDays })}</li>
                </ul>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('settings.account.deletion.confirmLabel')}
                </label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={t('settings.account.deletion.confirmPlaceholder')}
                  disabled={isLoadingDeletion}
                />
              </div>
              
              {deletionError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                  {deletionError}
                </div>
              )}
              
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                    setDeletionError(null);
                  }}
                  disabled={isLoadingDeletion}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onClick={handleRequestDeletion}
                  disabled={isLoadingDeletion || deleteConfirmText.toUpperCase() !== t('settings.account.deletion.confirmWord').toUpperCase()}
                >
                  {isLoadingDeletion ? t('common.loading') : t('settings.account.deletion.confirmButton')}
                </Button>
              </div>
            </div>
          ) : (
            // Initial state
            <div className="space-y-4">
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {t('settings.account.deletion.description')}
              </p>
              <div className="flex justify-end">
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  {t('settings.account.deletion.button')}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="space-y-8">
      {/* Default Account */}
      <Card>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {t('settings.preferences.defaultAccount.title')}
        </h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
          {t('settings.preferences.defaultAccount.description')}
        </p>
        <select
          value={localPreferences.defaultAccountId || ''}
          onChange={(e) => handleDefaultAccountChange(e.target.value)}
          disabled={isSaving || isLoadingPreferences}
          className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
        >
          <option value="">{t('settings.preferences.defaultAccount.none')}</option>
          {accounts.map(account => (
            <option key={account.id} value={account.id}>
              {account.name} - {account.bankName}
            </option>
          ))}
        </select>
      </Card>

      {/* Operations Page Size */}
      <Card>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {t('settings.preferences.pageSize.title')}
        </h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
          {t('settings.preferences.pageSize.description')}
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={PAGE_SIZE_MIN}
            max={PAGE_SIZE_MAX}
            value={localPreferences.operationsPageSize}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value)) {
                handlePageSizeChange(value);
              }
            }}
            disabled={isSaving || isLoadingPreferences}
            className="w-24 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 text-center focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
          />
          <span className="text-slate-500 dark:text-slate-400 text-sm">
            {t('settings.preferences.pageSize.perPage')} ({PAGE_SIZE_MIN}-{PAGE_SIZE_MAX})
          </span>
        </div>
      </Card>

      {/* Schedulers Page Size */}
      <Card>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {t('settings.preferences.schedulersPageSize.title')}
        </h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
          {t('settings.preferences.schedulersPageSize.description')}
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={PAGE_SIZE_MIN}
            max={PAGE_SIZE_MAX}
            value={localPreferences.schedulersPageSize}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value)) {
                handleSchedulersPageSizeChange(value);
              }
            }}
            disabled={isSaving || isLoadingPreferences}
            className="w-24 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 text-center focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
          />
          <span className="text-slate-500 dark:text-slate-400 text-sm">
            {t('settings.preferences.schedulersPageSize.perPage')} ({PAGE_SIZE_MIN}-{PAGE_SIZE_MAX})
          </span>
        </div>
      </Card>

      {/* Statistics Categories Count */}
      <Card>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {t('settings.preferences.statisticsCategoriesCount.title', 'Catégories dans les statistiques')}
        </h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
          {t('settings.preferences.statisticsCategoriesCount.description', 'Nombre de catégories affichées dans les camemberts de la page Statistiques')}
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={STATISTICS_CATEGORIES_MIN}
            max={STATISTICS_CATEGORIES_MAX}
            value={localPreferences.statisticsCategoriesCount}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value)) {
                handleStatisticsCategoriesCountChange(value);
              }
            }}
            disabled={isSaving || isLoadingPreferences}
            className="w-24 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 text-center focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
          />
          <span className="text-slate-500 dark:text-slate-400 text-sm">
            {t('settings.preferences.statisticsCategoriesCount.range', 'catégories')} ({STATISTICS_CATEGORIES_MIN}-{STATISTICS_CATEGORIES_MAX})
          </span>
        </div>
      </Card>

      {/* Dark Mode */}
      <Card>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
            {t('settings.preferences.darkMode.title')}
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
            {t('settings.preferences.darkMode.description')}
          </p>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={localPreferences.darkMode}
              onChange={(e) => handleDarkModeChange(e.target.checked)}
              disabled={isSaving || isLoadingPreferences}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 dark:peer-focus:ring-violet-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-violet-600"></div>
            <span className="ml-3 text-sm text-slate-600 dark:text-slate-400">
              {localPreferences.darkMode ? t('common.enabled', 'Activé') : t('common.disabled', 'Désactivé')}
            </span>
          </label>
        </div>
      </Card>

      {/* Success message */}
      {preferencesSuccess && (
        <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm">
          {t('settings.preferences.success')}
        </div>
      )}

      {/* Save/Cancel buttons */}
      <div className="flex justify-end gap-3">
        <Button
          variant="secondary"
          onClick={handlePreferencesCancel}
          disabled={!preferencesModified || isSaving}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handlePreferencesSave}
          disabled={!preferencesModified || isSaving}
        >
          {isSaving ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  if (isLoadingProfile && isLoadingPreferences) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-48 animate-pulse" />
        <div className="h-64 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('settings.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400">{t('settings.subtitle')}</p>
      </div>

      {/* Error messages */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Tabs */}
      {renderTabs()}

      {/* Tab Content */}
      {activeTab === 'account' && renderAccountTab()}
      {activeTab === 'preferences' && renderPreferencesTab()}
      {activeTab === 'subscription' && <SubscriptionTab />}
      {activeTab === 'alerts' && <AlertsTab />}
      {activeTab === 'categories' && canManageCategories() && <CategoriesPage embedded />}
      {activeTab === 'paymentMethods' && canManagePaymentMethods() && <PaymentMethodsPage embedded />}
      {activeTab === 'counterparties' && canManageCategories() && <CounterpartiesPage embedded />}
      {activeTab === 'sharedAccess' && canManageSharedAccess() && <SharedAccessTab />}
      {activeTab === 'apiKeys' && <ApiKeysPage />}
    </div>
  );
};

export default UserSettingsPage;
